/**
 * Note!
 * Pagination reset page to page number 1 is happening only when pageSize is selected - 10, 25, 50
 */

import { Logger } from 'vue-infra';
import { ACUserSettings } from '@core/services/ac/ACUserSettings';

const logger = Logger.getLogger('pagination');

export const NAMESPACE = 'pagination';

// An array of options for le-select component
export const perPageSelectDefaultOptions = [
  { id: '10', value: 10 },
  { id: '25', value: 25 },
  { id: '50', value: 50 },
];

/**
 * @param config
 * @param config.widgetName - widget name pagination is connected to
 * @param config.totalPages
 * @param config.currentPage
 * @param config.pageSize - used in calculating limit & offset query string for API calls
 * @param config.perPageOptions - used in le-select component to provide pageSize options
 */
export const paginationModule = (config) => {
  const { widgetName, totalPages, currentPage, pageSize, perPageOptions } = config;
  return {
    state: {
      widgetName,
      totalPages,
      currentPage,
      pageSize,
      perPageOptions,
      isLoading: false,
      initialized: false,
    },
    mutations: {
      /**
       * @param state - module's state
       * @param payload {Object} - paging config
       * @param payload.key {String} - pagination state prop to set
       * @param payload.value {Number|String} - prop value to be set
       */
      setPagination(state, { key, value }) {
        state[key] = value;
      },
    },
    actions: {
      /**
       * Initialises pagination by pulling from AC settings user saved settings
       * @param state - vuex local module's state
       * @param commit - vuex commit
       * @param moduleName {String} - module/widget's name
       * @return {Promise<void>}
       */
      async initializePagination({ state, commit }, moduleName) {
        if (!state.initialized) {
          commit('setPagination', { key: 'isLoading', value: true });
          const pSize = ACUserSettings.getPaginationPageSize(moduleName);
          if (pSize) {
            commit('setPagination', { key: 'pageSize', value: pSize });
          }
          commit('setPagination', { key: 'initialized', value: true });
          commit('setPagination', { key: 'isLoading', value: false });
        }
      },
      /**
       * @param context - Vuex context
       * @param payload {Object} - passed to dispatch
       * @param payload.widgetName {String} - widget name
       * @param payload.pagination {Object} - contains pagination config
       * @param payload.pagination.key {String} - state key totalPages/currentPage/perPage
       * @param payload.pagination.value {Number}
       * @param payload.storeCallback { Object }
       * @param payload.storeCallback.action { String } - action name to dispatch
       * @param payload.storeCallback.data { Object } - arguments to pass to callbackAction
       * @return {Promise<void>}
       */
      async changePagination({ state, commit, dispatch }, payload) {
        const { pagination: { key, value }, storeCallback } = payload;
        const widgetsName = state.widgetName;
        // prevent dispatching action when no value has changed
        if (state[key] !== value) {
          commit('setPagination', { key, value });
          // prevents callback action to be called when
          // action payload key is "totalPages"
          if (key !== 'totalPages' && storeCallback.action) {
            const callBackData = { ...storeCallback.data };
            if (key === 'pageSize') {
              // reset pagination current page to 1
              callBackData.resetPageNumber = true;
              // save changes to AC
              // context.state.widgetName used to save/load settings from AC
              ACUserSettings.savePaginationPageSize({
                pageSize: value,
                moduleName: widgetsName,
              })
                .catch(error => logger.error(
                  'save page size', `pagination::changePagination::${widgetsName}`,
                  error,
                ));
            }
            // dispatch store action to update/restart subscriptions/polling/etc
            await dispatch(storeCallback.action, callBackData);
          }
        }
      },
    },
    getters: {
      getPaginationConfigForAPI(state) {
        return {
          offset: ((state.currentPage - 1) * state.pageSize),
          limit: state.pageSize,
        };
      },
      getTotalPages(state) {
        return state.totalPages;
      },
    },
  };
};
